import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../api';

export default function ResetPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pw !== confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset-with-token', { token, new_password: pw });
      setDone(true);
      window.history.replaceState({}, '', '/reset-password');
      setTimeout(() => nav('/auth'), 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'This link is invalid or has expired.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-10">
      <div className="mx-auto max-w-md animate-fade-up">
        {done ? (
          <div className="rounded-3xl bg-white p-7 text-center card-soft" data-testid="reset-done">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent"><CheckCircle2 className="h-7 w-7 text-[hsl(var(--coral))]" /></div>
            <h1 className="mt-4 text-2xl font-extrabold">Password reset!</h1>
            <p className="mt-2 text-sm text-muted-foreground">Taking you to sign in…</p>
          </div>
        ) : !token ? (
          <div className="rounded-3xl bg-white p-7 text-center card-soft" data-testid="reset-invalid">
            <h1 className="text-2xl font-extrabold">Invalid link</h1>
            <p className="mt-2 text-sm text-muted-foreground">This reset link is missing or broken. Please request a new one.</p>
            <button onClick={() => nav('/forgot-password')} className="coral-btn mt-6 w-full rounded-2xl py-3.5 text-base font-bold text-white">Request new link</button>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold tracking-tight">Choose a new password</h1>
            <p className="mt-2 text-muted-foreground">Enter a new password for your Purrflect account.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <Label className="text-sm font-semibold">New password</Label>
                <div className="relative mt-1.5">
                  <Input type={showPw ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-password" className="h-12 rounded-xl bg-white pr-11" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password visibility">
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Confirm password</Label>
                <Input type={showPw ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" data-testid="reset-confirm" className="mt-1.5 h-12 rounded-xl bg-white" />
              </div>
              <button type="submit" disabled={busy} data-testid="reset-submit" className="coral-btn flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />}Set new password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
